import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MessageCircle, Plus, RefreshCw, Search, X } from 'lucide-react';
import '../styles/support-design.css';

type Faq = { id: number; category: string; question: string; answer: string; sort_order: number };

function catLabel(category: string) {
  const value = String(category || '').toLowerCase();
  if (value === 'general') return '일반';
  if (value === 'fee') return '수수료';
  if (value === 'cancel') return '취소/환불';
  if (value === 'payment') return '결제';
  if (value === 'contract') return '계약/서류';
  return category || '기타';
}

export default function FaqPage() {
  const [items, setItems] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);
  const [selectedCat, setSelectedCat] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch('/api/faqs', { signal: controller.signal });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok) throw new Error(data?.message || '질문을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        if (!controller.signal.aborted) setItems(Array.isArray(data.items) ? data.items : []);
      } catch (requestError) {
        if (!controller.signal.aborted) setError(requestError instanceof Error ? requestError.message : '질문을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [attempt]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) { const category = item.category || 'general'; counts.set(category, (counts.get(category) || 0) + 1); }
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter(item => {
      const category = item.category || 'general';
      if (selectedCat !== 'all' && selectedCat !== category) return false;
      return !search || `${item.question} ${item.answer} ${catLabel(category)}`.toLowerCase().includes(search);
    }).sort((a, b) => a.sort_order !== b.sort_order ? a.sort_order - b.sort_order : a.id - b.id);
  }, [items, selectedCat, query]);

  const resetFilters = () => { setQuery(''); setSelectedCat('all'); setOpenId(null); };
  const retry = () => { setLoading(true); setError(null); setAttempt(value => value + 1); };

  return <div className="jp-site jp-help-page jp-help-compact jp-faq-page">
    <header className="jp-wrap jp-help-heading"><h1>자주 묻는 질문</h1></header>
    <section className="jp-wrap jp-faq-content" aria-label="자주 묻는 질문">
      {loading ? <div className="jp-faq-feedback" role="status"><RefreshCw size={25} strokeWidth={1.5} /><h2>질문을 불러오고 있어요.</h2></div> : error ? <div className="jp-faq-feedback jp-faq-feedback-error" role="alert"><MessageCircle size={27} strokeWidth={1.5} /><h2>질문을 불러오지 못했어요.</h2><p>{error}</p><button type="button" className="jp-button jp-button-soft" onClick={retry}>다시 불러오기<RefreshCw size={16} /></button></div> : items.length === 0 ? <div className="jp-faq-feedback"><MessageCircle size={27} strokeWidth={1.5} /><h2>아직 등록된 질문이 없어요.</h2><p>궁금한 내용은 고객센터로 문의해 주세요.</p><Link to="/support" className="jp-text-link">고객센터 문의<ArrowUpRight size={16} /></Link></div> : <>
        <div className="jp-faq-search"><Search size={21} strokeWidth={1.6} aria-hidden="true" /><input type="search" aria-label="질문과 답변 검색" placeholder="어떤 내용이 궁금하세요?" value={query} onChange={event => { setQuery(event.target.value); setOpenId(null); }} />{query && <button type="button" onClick={() => { setQuery(''); setOpenId(null); }} aria-label="검색어 지우기"><X size={18} /></button>}</div>
        <div className="jp-faq-categories" role="group" aria-label="질문 주제 선택"><button type="button" aria-pressed={selectedCat === 'all'} onClick={() => { setSelectedCat('all'); setOpenId(null); }}>전체<span>{items.length}</span></button>{categories.map(([category, count]) => <button key={category} type="button" aria-pressed={selectedCat === category} onClick={() => { setSelectedCat(category); setOpenId(null); }}>{catLabel(category)}<span>{count}</span></button>)}</div>
        <p className="jp-faq-result-count" role="status" aria-live="polite">{query.trim() ? '검색 결과' : selectedCat === 'all' ? '전체 질문' : `${catLabel(selectedCat)} 질문`} <strong>{filtered.length}</strong>건</p>
        {filtered.length ? <div className="jp-faq-answers">{filtered.map(item => {
          const opened = openId === item.id;
          return <article className="jp-faq-answer" key={item.id}><h2><button id={`jp-faq-question-${item.id}`} type="button" aria-expanded={opened} aria-controls={`jp-faq-answer-${item.id}`} onClick={() => setOpenId(opened ? null : item.id)}><span className="jp-faq-question-mark" aria-hidden="true">Q</span><span><strong>{item.question}</strong></span><Plus size={19} className={opened ? 'is-open' : ''} aria-hidden="true" /></button></h2><div id={`jp-faq-answer-${item.id}`} role="region" aria-labelledby={`jp-faq-question-${item.id}`} hidden={!opened}><p>{item.answer}</p></div></article>;
        })}</div> : <div className="jp-faq-feedback"><Search size={28} strokeWidth={1.5} /><h2>검색 결과가 없어요.</h2><p>다른 검색어를 입력하거나 주제를 변경해 보세요.</p><button type="button" className="jp-text-link" onClick={resetFilters}>검색 조건 초기화<RefreshCw size={16} /></button></div>}
      </>}
    </section>
    <div className="jp-wrap jp-help-inline"><span>원하는 답변이 없나요?</span><Link to="/support">고객센터 문의<ArrowUpRight size={15} /></Link></div>
  </div>;
}
